import { AWTEventProperties, AWTPiiKind } from '@aria/webjs-sdk';

import { AriaService } from '../instrumentation/aria.service';
import { InstrumentationParam, InstrumentationService, KnownInstrumentationEvents } from '../instrumentation/instrumentation.service';
import { KnownPerformanceMarks, PerformanceService } from '../services/performance.service';
import { ClientStartupConfig } from '../shared-context/client-startup-config.service';

/**
 * This function will run when there is a fatal error during bootstrap of Angular, and we are unable to log it
 * through Angular's ErrorHandler class.
 * @param error the error object of the uncaught exception in bootstrap
 * @param startupConfig ClientStartupConfig object for initializing logger
 * @param logConsole Whether to log error in console
 */
export function onAngularBootstrapError(
    error: any,
    startupConfig: ClientStartupConfig,
    logConsole: boolean = true
) {
    if (logConsole) {
        // We do this explicitly here so if a customer reports an issue, we can still see the error in the console.
        console.error(error);
    }

    if (!startupConfig) {
        return;
    }

    // Construct Aria logger with minimal set of properties available via startup configuration
    const ariaLogger = AriaService.createAriaLogger(startupConfig);

    if (!ariaLogger) {
        return;
    }

    // One common 'Bootstrap' error we have seen is when zone.js's Promise is overwritten by another Promise polyfill
    // This will detect when that happens, without needing the stack trace, which can contain PII
    let isZoneError = false;
    try {
        Zone.assertZonePatched();
    } catch (ex) {
        isZoneError = true;
    }

    const queuedEvents = window.queuedEvents || [];
    const queuedMarks = window.queuedMarks;
    const appSecurityLevel = typeof window.windowsDataSecuritySetting === 'number' ? window.windowsDataSecuritySetting : DataSecurityLevel.Full;

    // Create performance measures
    // This code is copied in shared-inline.ts
    // TODO: Refactor to share generation of queuedEvents from queuedMarks in app and inline bundle
    // Start of copied content
    if (PerformanceService.performanceLoggingSupported(window)) {
        const logPerfEvent = (name: string, duration: number, params?: any) => {
            params = params || {};
            params[InstrumentationParam.Latency] = duration.toString();
            params[InstrumentationParam.ItemCategory] = 'Bootstrapper';
            const performanceEvent: QueuedAppEvent = { streamName: KnownInstrumentationEvents.PerformanceDump, eventName: name, eventParams: params, metrics: [], securityThreshold: DataSecurityLevel.Security };
            queuedEvents.push(performanceEvent);
        };

        const logPerfError = (errorName: string, errorDetails: string, params?: any) => {
            params = params || {};
            params[InstrumentationParam.Error] = errorName;
            params[InstrumentationParam.DebugInfo] = errorDetails;
            params[InstrumentationParam.ItemCategory] = 'Bootstrapper';
            const errorEvent: QueuedAppEvent = { streamName: KnownInstrumentationEvents.GenericEvent, eventName: KnownInstrumentationEvents.PerformanceMarkFailure, eventParams: params, metrics: [], securityThreshold: DataSecurityLevel.Security };
            queuedEvents.push(errorEvent);
        };

        if (queuedMarks) {
            queuedMarks.splice(0, queuedMarks.length).forEach( mark => {
                const startMark = mark.measureFrom || KnownPerformanceMarks.NavigationStart;

                // BUGBUG: The following call may throw exception in case startMark value is deemed to be invalid. This seem to
                // primarily happen on Edge/IE and Firefox. Wrapping into try/catch so we have direct way of checking how often it happens.
                // Consider removing try/catch in case this is no longer happening.
                try {
                    window.performance.measure(mark.markName, startMark, mark.markName);
                } catch (ex) {
                    logPerfError(mark.markName, `Failed to record <${mark.markName}> starting from <${startMark}>: exception is thrown`, mark.eventParams);
                    return;
                }

                const appMeasures = window.performance.getEntriesByName(mark.markName, 'measure');
                if (!appMeasures || !appMeasures.length) {
                    logPerfError(mark.markName, `Failed to record <${mark.markName}>: measure missing`, mark.eventParams);
                    return;
                }

                const timingEntry = appMeasures[0];
                const duration = Math.ceil(timingEntry.duration);
                logPerfEvent(mark.markName, duration, mark.eventParams);
            });
        }

        const browserMeasures = PerformanceService.getBrowserTimingMeasures(
            window,
            (errorName, errorDetails) => {
                logPerfError(errorName, errorDetails);
            }
        );
        browserMeasures.forEach( measure => {
            logPerfEvent(measure.name, measure.duration);
        });
    }
    // End of copied content

    // Log all queued events before flush
    if (queuedEvents.length > 0) {
        queuedEvents.forEach(queuedEvent => {
            const eventSecurityThreshold = queuedEvent.securityThreshold || DataSecurityLevel.Security;
            if (eventSecurityThreshold > appSecurityLevel) {
                return;
            }

            // NOTE: the type of log events should be (AWTEventProperties | AWTEventData | string)
            const ariaProps = new AWTEventProperties();
            const params = queuedEvent.eventParams;

            if (params) {
                for (const key in params) {
                    if (params.hasOwnProperty(key)) {
                        ariaProps.setProperty(key, params[key]);
                    }
                }
            }
            ariaProps.setName(queuedEvent.streamName);
            ariaProps.setProperty('EventName', queuedEvent.eventName);
            ariaProps.setProperty('AppSecurityLevel', appSecurityLevel);
            ariaLogger.logEvent(ariaProps);
        });
        window.queuedEvents = [];
    }

    const stackTrace = (error && error.stack) || 'Browser did not provide stack trace';
    let errorMessage = (error && error.message) || 'No Error Message Provided';
    if (errorMessage.lastIndexOf('.') !== errorMessage.length - 1) {
        errorMessage += '.';
    }
    const errorInfo = `Fatal error preventing Angular bootstrap: ${errorMessage} Zone error: ${isZoneError}`;

    const createEvent = (name: KnownInstrumentationEvents) => {
        const ev = new AWTEventProperties();
        ev.setName(name);
        ev.setProperty(InstrumentationParam.EventName, KnownInstrumentationEvents.UnhandledException);
        ev.setProperty(InstrumentationParam.ItemCategory, 'Bootstrap');
        ev.setProperty(InstrumentationParam.Error, InstrumentationService.filterPii(errorInfo));
        ev.setProperty(InstrumentationParam.AppSecurityLevel, appSecurityLevel);

        return ev;
    };

    const event = createEvent(KnownInstrumentationEvents.GenericEvent);

    if (startupConfig.logPiiAllowed) {
        event.setPropertyWithPii(InstrumentationParam.DebugInfo, stackTrace, startupConfig.logPiiInClearText ? AWTPiiKind.NotSet : AWTPiiKind.GenericData);
    }

    ariaLogger.logEvent(event);

    const debugCacheEvent = createEvent(KnownInstrumentationEvents.GenericDebugCache);
    debugCacheEvent.setProperty(InstrumentationParam.DebugInfo, stackTrace);
    ariaLogger.logEvent(debugCacheEvent);

    AriaService.endSession(ariaLogger, true);
}
